import { ScenarioDefinitionType } from '../../api/scenarioDefinitions';
import { TemplateType } from '../../api/types/scenario';
import styles from './EditScenarioMessages.module.css';
import EditableTemplateMessage from './EditableTemplateMessage';
import { sortIcon, pencilIcon } from '../../assets/icons';
import { useState, useEffect, useRef } from 'react';
import SortableTemplateMessage from './SortableTemplateMessage';
import { useDevice } from '../../context/DeviceContext';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import ScenarioScriptEditor from './ScenarioScriptEditor';

// Create a StrictModeDroppable component to fix issues with React 18 strict mode
const StrictModeDroppable = ({ children, ...props }: React.ComponentProps<typeof Droppable>) => {
  const [enabled, setEnabled] = useState(false);
  
  useEffect(() => {
    // This is needed for React 18 Strict Mode compatibility
    const animation = requestAnimationFrame(() => setEnabled(true));
    
    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);
  
  if (!enabled) {
    // Return a placeholder that won't cause layout shifts
    return (
      <div className={styles.droppableContainer}>
        {/* Placeholder with the same styling */}
      </div>
    );
  }
  
  return <Droppable {...props}>{children}</Droppable>;
};

interface EditScenarioMessagesProps {
  definition: ScenarioDefinitionType;
  onEditMessage: (messageId: string, updates: {
    templateType?: TemplateType;
    characterId?: string;
    content?: string;
  }) => Promise<void>;
  onAddMessage: (templateType: TemplateType, characterId: string, content: string) => Promise<void>;
  onDeleteMessage: (messageId: string) => Promise<void>;
  setDefinition: React.Dispatch<React.SetStateAction<ScenarioDefinitionType>>;
}

function EditScenarioMessages({ definition, onEditMessage, onAddMessage, onDeleteMessage, setDefinition }: EditScenarioMessagesProps): JSX.Element {
  const [isSortMode, setIsSortMode] = useState(false);
  const [sortedMessages, setSortedMessages] = useState<string[]>([]);
  const { isMobile } = useDevice();
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const addMenuRef = useRef<HTMLDivElement>(null);
  const [isScriptEditorOpen, setIsScriptEditorOpen] = useState(false);

  // Initialize sorted messages when sort mode is activated
  const toggleSortMode = () => {
    if (!isSortMode && definition.messages) {
      // Initialize with current message order
      setSortedMessages(definition.messages.map(msg => msg.id));
    }
    setIsSortMode(!isSortMode);
  };

  const handleAddMessage = () => {
    // Toggle the add menu instead of immediately adding a message
    setIsAddMenuOpen(!isAddMenuOpen);
  };

  const handleAddMessageType = (templateType: TemplateType) => {
    // Add message with the selected template type
    let characterId = '';
    if (templateType === 'character' && definition.characters.length > 0) {
      characterId = definition.characters[0].id;
    }
    
    onAddMessage(templateType, characterId, 'New message');
    setIsAddMenuOpen(false);
  };

  // Handle click outside of add menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addMenuRef.current && !addMenuRef.current.contains(event.target as Node)) {
        setIsAddMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle drag end in queue
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    
    const items = Array.from(sortedMessages);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSortedMessages(items);
  };

  const saveSortOrder = async () => {
    try {
      const updatedDefinition = await definition.reorderMessages(sortedMessages);
      setDefinition(updatedDefinition);
      setIsSortMode(false);
    } catch (error) {
      console.error('Failed to save message order:', error);
    }
  };

  const cancelSortMode = () => {
    setIsSortMode(false);
  };

  const handleOpenScriptEditor = () => {
    setIsScriptEditorOpen(true);
  };

  const handleCloseScriptEditor = () => {
    setIsScriptEditorOpen(false);
  };

  return (
    <div className={`${styles.editMessagesContainer} ${isSortMode ? styles.sortModeActive : ''}`}>
      <div className={`${styles.sectionHeader} ${isMobile ? styles.mobileHeader : ''}`}>
        <h3 className={styles.sectionTitle}>Messages</h3>
        <div className={styles.headerButtons}>
          {isSortMode ? (
            <>
              <button 
                onClick={cancelSortMode}
                className={`${styles.iconButton} ${styles.cancelButton}`}
                aria-label="Cancel sorting"
              >
                Cancel
              </button>
              <button 
                onClick={saveSortOrder}
                className={`${styles.iconButton} ${styles.saveButton}`}
                aria-label="Save order"
              >
                Save Order
              </button>
            </>
          ) : (
            <>
              <button 
                onClick={handleOpenScriptEditor}
                className={styles.iconButton}
                aria-label="Edit script"
              >
                <img src={pencilIcon} alt="Edit" className={styles.buttonIcon} />
              </button>
              <button 
                onClick={toggleSortMode}
                className={`${styles.iconButton} ${isSortMode ? styles.activeIconButton : ''}`}
                aria-label="Sort messages"
              >
                <img src={sortIcon} alt="Sort" className={styles.buttonIcon} />
              </button>
              <div className={styles.addMessageContainer}>
                <button 
                  onClick={handleAddMessage}
                  className={styles.addButton}
                  aria-label="Add new message"
                >
                  +
                </button>
                {isAddMenuOpen && (
                  <div ref={addMenuRef} className={styles.addMessageDropdown}>
                    <div className={styles.addMessageOption} data-type="narrator" onClick={() => handleAddMessageType('narrator')}>
                      <span>Narrator</span>
                    </div>
                    <div className={styles.addMessageOption} data-type="character" onClick={() => handleAddMessageType('character')}>
                      <span>Character</span>
                    </div>
                    <div className={styles.addMessageOption} data-type="user-slot" onClick={() => handleAddMessageType('user-slot')}>
                      <span className={styles.templateCode}>{'{{user}}'}</span>
                    </div>
                    <div className={styles.addMessageOption} data-type="char-slot" onClick={() => handleAddMessageType('char-slot')}>
                      <span className={styles.templateCode}>{'{{char}}'}</span>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      
      <div className={styles.messagesList}>
        {definition.messages && definition.messages.length > 0 ? (
          isSortMode ? (
            // Render sortable messages when in sort mode
            <DragDropContext onDragEnd={handleDragEnd}>
              <StrictModeDroppable droppableId="messages">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className={styles.droppableContainer}
                  >
                    {sortedMessages.map((messageId, index) => {
                      const message = definition.messages.find(msg => msg.id === messageId);
                      if (!message) return null;
                      
                      return (
                        <Draggable key={message.id} draggableId={message.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={styles.draggableMessage}
                              // react-beautiful-dnd will add data-rbd-dragging-id when dragging
                              // which we can use for styling without animation conflicts
                            >
                              <SortableTemplateMessage
                                message={message}
                                index={index}
                                moveMessage={() => {}}
                              />
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </StrictModeDroppable>
            </DragDropContext>
          ) : (
            // Render regular editable messages
            definition.messages.map((message) => (
              <EditableTemplateMessage 
                key={message.id} 
                message={message}
                characters={definition.characters}
                onEdit={onEditMessage}
                onDelete={onDeleteMessage}
              />
            ))
          )
        ) : (
          <div className={styles.emptyState}>
            <p>No template messages have been added to this scenario yet.</p>
          </div>
        )}
      </div>

      {isScriptEditorOpen && (
        <ScenarioScriptEditor 
          definition={definition}
          setDefinition={setDefinition}
          onClose={handleCloseScriptEditor}
        />
      )}
    </div>
  );
}

export default EditScenarioMessages; 